import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Crown, Check, Zap, Music, Layers, Headphones, Settings, Star, Sparkles, AlertCircle, ArrowRight, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const UpgradePage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [foundersAvailability, setFoundersAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(true);

  // Fetch founders availability on load
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await api.get('/founders/availability');
        setFoundersAvailability(response.data);
      } catch (error) {
        console.error('Error fetching founders availability:', error);
        setFoundersAvailability({ max: 50, sold: 0, remaining: 50, is_sold_out: false });
      } finally {
        setLoadingAvailability(false);
      }
    };
    
    fetchAvailability();
  }, []);

  const handleCheckout = async (planType) => {
    setLoadingPlan(planType);
    try {
      const response = await api.post('/stripe/create-checkout-session', { plan_type: planType });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      
      if (error.response?.status === 409 && error.response?.data?.detail?.error === 'FOUNDERS_SOLD_OUT') {
        toast.error('Founders spots just sold out! Please choose Monthly or Annual instead.', {
          duration: 5000,
          icon: <AlertCircle className="w-5 h-5" />
        });
        try {
          const refreshResponse = await api.get('/founders/availability');
          setFoundersAvailability(refreshResponse.data);
        } catch (e) {}
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to start checkout');
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const response = await api.post('/stripe/create-portal-session');
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to open billing portal. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoadingPortal(false);
    }
  };

  const features = [
    { icon: Layers, text: 'Unlimited copedants' },
    { icon: Music, text: 'Full chord finder' },
    { icon: Headphones, text: 'Shed tracks & practice loops' },
    { icon: Zap, text: 'Advanced grip filters' },
    { icon: Sparkles, text: 'Future updates included' },
    { icon: Star, text: 'Priority support' },
  ];

  const isPro = user?.is_pro;
  const proType = user?.pro_type;
  const isLifetime = proType === 'lifetime';
  const isMonthly = proType === 'monthly';
  const isAnnual = proType === 'annual';
  
  // Founders availability
  const isSoldOut = foundersAvailability?.is_sold_out || false;
  const foundersSold = foundersAvailability?.sold || 0;
  const foundersRemaining = foundersAvailability?.remaining || 0;

  // Helper to get the page title
  const getPageTitle = () => {
    if (!isPro) return 'Upgrade to Pro';
    if (isLifetime) return 'Founders Status';
    return 'Manage Subscription';
  };

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="text-ss-muted hover:text-ss-text"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="ml-4">
            <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
              {getPageTitle()}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12 animate-slide-up">
          <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-gradient-to-br from-ss-gold/20 to-ss-gold/10 border border-ss-gold/30 flex items-center justify-center">
            <Crown className="w-10 h-10 text-ss-gold" />
          </div>
          
          {isPro ? (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-ss-cyan/20 border border-ss-cyan/40 rounded-full mb-4">
                <Check className="w-5 h-5 text-ss-cyan" />
                <span className="text-ss-cyan font-semibold">
                  {isLifetime ? 'Founders Lifetime' : isMonthly ? 'Pro Monthly' : 'Pro Annual'}
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase text-ss-text mb-4">
                {isLifetime ? "Founders Forever" : "You're Pro!"}
              </h1>
              <p className="text-ss-muted text-lg max-w-xl mx-auto">
                {isLifetime 
                  ? "You have lifetime access. No billing, no renewals—ever."
                  : isMonthly
                    ? "Monthly subscription active. Switch plans or upgrade to Lifetime anytime."
                    : "Annual subscription active. Switch plans or upgrade to Lifetime anytime."}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter uppercase text-ss-text mb-4">
                Unlock Full Access
              </h1>
              <p className="text-ss-muted text-lg max-w-xl mx-auto">
                Practice smarter. Play better. Explore your instrument.
              </p>
            </>
          )}
        </div>

        {/* ===============================
            FREE USER: Show all 3 plans
        =============================== */}
        {!isPro && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {/* Monthly Plan */}
            <Card className="bg-ss-surface border-ss-border relative">
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-xl text-ss-text">
                  Pro Monthly
                </CardTitle>
                <CardDescription className="text-ss-muted text-sm">
                  Full access. Cancel anytime.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6 py-2">
                  <span className="text-4xl font-heading font-black text-ss-text">$9</span>
                  <span className="text-ss-muted"> /month</span>
                </div>

                <Button
                  onClick={() => handleCheckout('monthly')}
                  disabled={loadingPlan !== null}
                  data-testid="checkout-monthly-btn"
                  className="w-full h-11 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
                >
                  {loadingPlan === 'monthly' ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Start Monthly — $9/mo'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Annual Plan - BEST VALUE */}
            <Card className="bg-ss-surface border-ss-cyan relative overflow-hidden transform md:scale-105 shadow-lg shadow-ss-cyan/10">
              <div className="absolute top-0 left-0 right-0 bg-ss-cyan text-black text-xs font-bold text-center py-1.5 tracking-wider">
                BEST VALUE — SAVE 33%
              </div>
              
              <CardHeader className="text-center pb-2 pt-10">
                <CardTitle className="font-heading text-xl text-ss-cyan flex items-center justify-center gap-2">
                  <Crown className="w-5 h-5" />
                  Pro Annual
                </CardTitle>
                <CardDescription className="text-ss-muted text-sm">
                  Save 33% with annual billing.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6 py-2">
                  <span className="text-5xl font-heading font-black text-ss-text">$72</span>
                  <span className="text-ss-muted"> /year</span>
                </div>

                <Button
                  onClick={() => handleCheckout('annual')}
                  disabled={loadingPlan !== null}
                  data-testid="checkout-annual-btn"
                  className="w-full h-12 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-bold text-base"
                >
                  {loadingPlan === 'annual' ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Go Pro — $72/year
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Founders Lifetime Plan */}
            <Card className={`bg-ss-surface relative overflow-hidden ${isSoldOut ? 'border-ss-muted/30 opacity-75' : 'border-ss-gold/30'}`}>
              {!isSoldOut && (
                <div className="absolute top-3 right-3 px-2 py-0.5 bg-ss-gold/10 border border-ss-gold/20 text-ss-gold text-[9px] font-medium rounded tracking-wide uppercase">
                  Founders
                </div>
              )}
              {isSoldOut && (
                <div className="absolute top-3 right-3 px-2 py-0.5 bg-ss-muted/20 border border-ss-muted/30 text-ss-muted text-[9px] font-medium rounded tracking-wide uppercase">
                  Sold Out
                </div>
              )}
              
              {!isSoldOut && (
                <div className="absolute inset-0 bg-gradient-to-br from-ss-gold/5 to-transparent pointer-events-none"></div>
              )}
              
              <CardHeader className="text-center pb-2 relative">
                <CardTitle className={`font-heading text-xl flex items-center justify-center gap-2 ${isSoldOut ? 'text-ss-muted' : 'text-ss-gold'}`}>
                  <Star className="w-5 h-5" />
                  Founders Lifetime
                </CardTitle>
                <CardDescription className="text-ss-muted text-sm">
                  {isSoldOut 
                    ? 'All spots have been claimed.'
                    : 'One-time payment. Lifetime access.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center relative">
                <div className="mb-2 py-2">
                  <span className={`text-4xl font-heading font-black ${isSoldOut ? 'text-ss-muted line-through' : 'text-ss-text'}`}>$99</span>
                  <span className="text-ss-muted"> one-time</span>
                </div>
                
                {!isSoldOut && !loadingAvailability && (
                  <div className="mb-4">
                    <span className="inline-block px-2.5 py-1 bg-ss-gold/10 border border-ss-gold/20 rounded-full text-[11px] text-ss-gold/80">
                      {foundersSold} of 50 claimed
                    </span>
                  </div>
                )}
                {isSoldOut && (
                  <div className="mb-4">
                    <span className="inline-block px-2.5 py-1 bg-ss-muted/10 border border-ss-muted/20 rounded-full text-[11px] text-ss-muted">
                      50 of 50 claimed
                    </span>
                  </div>
                )}

                <Button
                  onClick={() => handleCheckout('lifetime')}
                  disabled={loadingPlan !== null || isSoldOut}
                  data-testid="checkout-lifetime-btn"
                  className={`w-full h-11 font-semibold ${
                    isSoldOut 
                      ? 'bg-ss-muted/10 text-ss-muted border border-ss-muted/30 cursor-not-allowed' 
                      : 'bg-ss-gold/20 hover:bg-ss-gold/30 border border-ss-gold/40 text-ss-gold'
                  }`}
                >
                  {loadingPlan === 'lifetime' ? (
                    <div className="w-5 h-5 border-2 border-ss-gold border-t-transparent rounded-full animate-spin" />
                  ) : isSoldOut ? (
                    'Sold Out'
                  ) : (
                    <>
                      <Star className="w-4 h-4 mr-2" />
                      Get Founders — $99
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===============================
            LIFETIME USER: Status only
        =============================== */}
        {isLifetime && (
          <div className="mb-12">
            <Card className="bg-ss-surface border-ss-gold/30 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-ss-gold/5 to-transparent pointer-events-none"></div>
              <CardContent className="py-10 text-center relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-ss-gold/20 border border-ss-gold/30 flex items-center justify-center">
                  <Star className="w-8 h-8 text-ss-gold" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-ss-gold mb-2">
                  Founders Lifetime Access
                </h2>
                <p className="text-ss-muted mb-6 max-w-md mx-auto">
                  You're one of only 50 Founders. No subscription, no renewals—your access never expires.
                </p>
                
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-ss-surface border border-ss-border rounded-lg text-ss-muted text-sm">
                  <CreditCard className="w-4 h-4" />
                  No billing to manage (Lifetime)
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===============================
            MONTHLY/ANNUAL USER: Manage & Switch
        =============================== */}
        {isPro && !isLifetime && (
          <div className="space-y-6 mb-12">
            {/* Current Plan Card */}
            <Card className="bg-ss-surface border-ss-cyan/30">
              <CardContent className="py-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 mb-2">
                      <Crown className="w-6 h-6 text-ss-cyan" />
                      <span className="text-xl font-heading font-bold text-ss-text">
                        {isMonthly ? 'Monthly Pro' : 'Annual Pro'}
                      </span>
                    </div>
                    {user?.pro_current_period_end && (
                      <p className="text-ss-muted text-sm">
                        Renews {new Date(user.pro_current_period_end).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    onClick={handleManageBilling}
                    disabled={loadingPortal}
                    data-testid="manage-billing-btn"
                    className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold h-11 px-6"
                  >
                    {loadingPortal ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Billing
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Plan Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Switch to other subscription plan */}
              <Card className={`bg-ss-surface ${isMonthly ? 'border-ss-cyan/30' : 'border-ss-border'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg text-ss-text flex items-center gap-2">
                    <ArrowRight className="w-5 h-5 text-ss-cyan" />
                    {isMonthly ? 'Switch to Annual' : 'Switch to Monthly'}
                  </CardTitle>
                  <CardDescription className="text-ss-muted text-sm">
                    {isMonthly 
                      ? 'Save 33% with annual billing ($72/year)'
                      : 'Pay monthly ($9/month) for flexibility'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleManageBilling}
                    disabled={loadingPortal}
                    data-testid="switch-plan-btn"
                    variant="outline"
                    className="w-full border-ss-border hover:border-ss-cyan/50 text-ss-text"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Change Plan in Portal
                  </Button>
                  <p className="text-xs text-ss-muted mt-2 text-center">
                    Opens Stripe billing portal
                  </p>
                </CardContent>
              </Card>

              {/* Upgrade to Lifetime */}
              <Card className={`bg-ss-surface relative overflow-hidden ${isSoldOut ? 'border-ss-muted/30 opacity-75' : 'border-ss-gold/30'}`}>
                {!isSoldOut && (
                  <div className="absolute inset-0 bg-gradient-to-br from-ss-gold/5 to-transparent pointer-events-none"></div>
                )}
                <CardHeader className="pb-2 relative">
                  <CardTitle className={`font-heading text-lg flex items-center gap-2 ${isSoldOut ? 'text-ss-muted' : 'text-ss-gold'}`}>
                    <Star className="w-5 h-5" />
                    Upgrade to Founders Lifetime
                  </CardTitle>
                  <CardDescription className="text-ss-muted text-sm">
                    {isSoldOut 
                      ? 'All 50 Founders spots have been claimed.'
                      : `One-time $99. ${foundersRemaining} of 50 spots left.`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative">
                  <Button
                    onClick={() => handleCheckout('lifetime')}
                    disabled={loadingPlan !== null || isSoldOut}
                    data-testid="upgrade-lifetime-btn"
                    className={`w-full h-11 font-semibold ${
                      isSoldOut 
                        ? 'bg-ss-muted/10 text-ss-muted border border-ss-muted/30 cursor-not-allowed' 
                        : 'bg-ss-gold hover:bg-ss-gold/90 text-black'
                    }`}
                  >
                    {loadingPlan === 'lifetime' ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : isSoldOut ? (
                      'Sold Out'
                    ) : (
                      <>
                        <Star className="w-4 h-4 mr-2" />
                        Get Founders — $99
                      </>
                    )}
                  </Button>
                  {!isSoldOut && (
                    <p className="text-xs text-ss-muted mt-2 text-center">
                      Your current subscription will be cancelled
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Features List */}
        <div className="mb-12">
          <h2 className="font-heading text-lg font-bold text-ss-text mb-6 text-center">
            Everything included with Pro
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-4 bg-ss-surface border border-ss-border rounded-lg"
              >
                <div className="w-9 h-9 rounded-lg bg-ss-cyan/10 border border-ss-cyan/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-ss-cyan" />
                </div>
                <span className="text-sm text-ss-text">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table - Only show for free users */}
        {!isPro && (
          <Card className="bg-ss-surface border-ss-border mb-12">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-ss-text">
                Free vs Pro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ss-border">
                      <th className="py-3 text-left text-ss-muted text-sm">Feature</th>
                      <th className="py-3 text-center text-ss-muted text-sm">Free</th>
                      <th className="py-3 text-center text-ss-cyan text-sm">Pro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-ss-border/50">
                      <td className="py-4 text-ss-text">Saved Copedants</td>
                      <td className="py-4 text-center text-ss-muted">1</td>
                      <td className="py-4 text-center text-ss-cyan font-semibold">Unlimited</td>
                    </tr>
                    <tr className="border-b border-ss-border/50">
                      <td className="py-4 text-ss-text">Chord Finder Results</td>
                      <td className="py-4 text-center text-ss-muted">10</td>
                      <td className="py-4 text-center text-ss-cyan font-semibold">Unlimited</td>
                    </tr>
                    <tr className="border-b border-ss-border/50">
                      <td className="py-4 text-ss-text">Advanced Grip Filters</td>
                      <td className="py-4 text-center text-ss-muted">—</td>
                      <td className="py-4 text-center"><Check className="w-5 h-5 text-ss-cyan mx-auto" /></td>
                    </tr>
                    <tr className="border-b border-ss-border/50">
                      <td className="py-4 text-ss-text">Shed Tracks (Full)</td>
                      <td className="py-4 text-center text-ss-muted">30s preview</td>
                      <td className="py-4 text-center"><Check className="w-5 h-5 text-ss-cyan mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-4 text-ss-text">Priority Support</td>
                      <td className="py-4 text-center text-ss-muted">—</td>
                      <td className="py-4 text-center"><Check className="w-5 h-5 text-ss-cyan mx-auto" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ / Contact */}
        <div className="text-center">
          <p className="text-ss-muted text-sm">
            Questions? Contact us at{' '}
            <a href="mailto:support@streaklabs.com" className="text-ss-cyan hover:underline">
              support@streaklabs.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default UpgradePage;
